import React, { useState } from 'react';
import { GridRow, GridCol, Button, Link } from '@lmig/lmds-react';
import PropTypes from 'prop-types';
import css from 'styled-jsx/css';
import cx from 'classnames';
import Image from '@lmig/dotcom-aspect-components/Utility/Image';
import Heading from '@lmig/dotcom-aspect-components/Utility/Heading';
import YouTube from '@lmig/dotcom-aspect-components/Utility/YouTube';
import LinkEnvironmentalize from '@lmig/dotcom-aspect-components/LinkEnvironmentalize';
import useThrottledHandler from '@lmig/dotcom-aspect-hooks/useThrottledHandler';
import TextBody from '@lmig/dotcom-aspect-components/Utility/TextBody';
import breakpoints from '@lmig/lmds-tokens/dist/json/core/breakpoint.json';


const getRowFlexStyles = (mobileImageFirst, desktopImageFirst) => {
  if (!mobileImageFirst && desktopImageFirst) {
    return css.resolve`
      .lm-Row {
        flex-flow: row-reverse wrap;
      }
    `;
  }

  if (mobileImageFirst && !desktopImageFirst) {
    return css.resolve`
      .lm-Row {
        flex-flow: row wrap-reverse;
      }
    `;
  }

  if (mobileImageFirst && desktopImageFirst) {
    return css.resolve`
      .lm-Row {
        flex-flow: row-reverse wrap-reverse;
      }
    `;
  }

  return { className: '', styles: '' };
};

const getRowStyles = (noTopPadding) => {
  const topPadding = noTopPadding ? 0 : '2rem';

  return css.resolve`
    .lm-Row {
      @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min}) {
        justify-content: space-between;
        padding-top: ${topPadding};
        padding-bottom: 1.5rem;
      }
    }
  `;
};

const getButtonStyles = () => css.resolve`
    * {
      display: block;
      width: 100%;
      margin-bottom: 0.5rem;
      min-width: 16rem;
      @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min}) {
        display: inline-block;
        width: auto;
        margin-right: 0.75rem;
      }
    }
  `;

const getWidthStyle = (textWidth) => {
  if (textWidth === 'textWrap') {
    return css.resolve`
      * {
        width: 92%;

        @media (min-width: 68.75rem) {
          width: 80%;
        }
      }
    `;
  }

  return css.resolve`
      * {
        width: 100%;
      }
    `;

};

const getImgWrapperClassStyles = ({ detachedImage, useImageDimensions, imageHeight, imageWidth }) => {
  if (detachedImage) {
    return [
      css.resolve`

      * {
        @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min}) {
          position: relative;
        }
      }
    `,
      css.resolve`

      * {
        @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min}) {
          position: absolute;
          left: 0;
          top: 0;
        }
      }
    `,
    ];
  }

  if (useImageDimensions && imageHeight && imageWidth) {
    return [
      css.resolve`
        * {
          height: ${imageHeight}px;
          width: ${imageWidth}px;
        }
      `,
      { className: '', styles: '' }];
  }

  return [{ className: '', styles: '' }, { className: '', styles: '' }];
};

const imageStyle = (imageRounded) => {
  if (imageRounded) {
    return css.resolve`
      * {
        border-radius: 3rem;
      }
    `;
  }

  return { className: '', styles: '' };
};

const getFontStyle = (fontSize, claimsTextStyle) => {
  if (fontSize) {
    return css.resolve`
    .lm-Body {
        @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min} ){
          font-size: 1.25rem;
        }
      }
    `;
  }

  if (claimsTextStyle) {
    return css.resolve`
    .lm-Body {
       @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min} ){
          width: 35rem;
          margin-left: -6rem;
        }

        @media screen and (min-width: ${breakpoints.lmig.breakpoint.lg.min} ){
          width: 100%;
          margin-left: 0;
        }
      }
    `;
  }

  return { className: '', styles: '' };
};

const getClaimsStyle = (claimsStyle) => {
  if (claimsStyle) {
    return css.resolve`
    .lm-Row {
        display: flex;
        flex-direction: column-reverse;

        @media screen and (min-width: ${breakpoints.lmig.breakpoint.lg.min} ){
          width: 52rem;
          flex-direction: row-reverse;
          padding: 2.5rem 0;
        }
      }

      .lm-Heading {
        @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min} ){
          width: 35rem;
          margin-left: -6rem;
        }
        @media screen and (min-width: ${breakpoints.lmig.breakpoint.lg.min} ){
          width: 30rem;
          margin-left: 0;
        }
        @media screen and (min-width: ${breakpoints.lmig.breakpoint.xl.min} ){
          width: 42rem;
        }
      }

      .lm-Col {
        padding-bottom: 3rem;

        @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min} ){
            margin-left: -11rem;
        }

        @media screen and (min-width: ${breakpoints.lmig.breakpoint.lg.min} ){
            padding-left: 2rem;
            padding-bottom: 0;
        }
      }
    `;
  }

  return { className: '', styles: '' };
};

const getImageStyle = (claimsImageStyle) => {
  if (claimsImageStyle) {
    return css.resolve`
    .image {
       @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min} ){
          width: 30rem;
        }

        @media screen and (min-width: ${breakpoints.lmig.breakpoint.lg.min} ){
          width: 320px;
          height: 290px;
        }
      }
    `;
  }

  return { className: '', styles: '' };
};

// eslint-disable-next-line complexity
const ArticleWithImage = ({
  title = undefined,
  text = undefined,
  image = undefined,
  mobileImageFirst = false,
  desktopImageFirst = false,
  mobileTitleBeforeImage = false,
  subHeading = undefined,
  buttons = [],
  showButtonsOnDesktop = true,
  showLinkOnMobile = true,
  headingType = 'h4',
  headingTag = 'h4',
  textWidth = 'normal',
  mobileBreakpoint = 'md',
  alt = '',
  linkHref = '',
  linkText = '',
  themeEvolved = false,
  imageColumns = 6,
  contentColumns = 6,
  detachedImage = false,
  imageDomain = undefined,
  children = undefined,
  imageHeight = undefined,
  imageWidth = undefined,
  noTopPadding = false,
  videoId = undefined,
  imageRounded = false,
  useImageDimensions = false,
  preloadImage = false,
  headingMarginTop = '',
  fontSize = false,
  claimsStyle = false,
  claimsImageStyle = false,
  claimsTextStyle = false,
  ...rest
}) => {

  const [viewportWidth, setViewportWidth] = useState(1280);

  useThrottledHandler(
    'resize',
    () => {
      setViewportWidth(window.innerWidth);
    },
    { callOnInit: true },
  );

  const isMobile = viewportWidth < { sm: 480, md: 768, lg: 960, xl: 1280 }[mobileBreakpoint];


  const { className: rowFlexClass, styles: rowFlexStyles } = getRowFlexStyles(
    mobileImageFirst,
    desktopImageFirst,
  );
  const { className: rowClass, styles: rowStyles } = getRowStyles(noTopPadding);
  const { className: btnClass, styles: btnStyles } = getButtonStyles();
  const { className: textClass, styles: textStyles } = getWidthStyle(textWidth);
  const [
    { className: imgWrapperClass, styles: imgWrapperStyles },
    { className: imgClass, styles: imgStyles },
  ] = getImgWrapperClassStyles({ detachedImage, useImageDimensions, imageHeight, imageWidth });
  const { className: bodyClass, styles: bodyStyles } = css.resolve`
    * {
      margin-bottom: 25px;
    }
  `;
  const { className: imageClass, styles: imageStyles } = imageStyle(imageRounded);
  const { className: fontClass, styles: fontStyles } = getFontStyle(fontSize, claimsTextStyle);
  const { className: claimsClass, styles: claimsStyles } = getClaimsStyle(claimsStyle);
  const { className: claimsImageClass, styles: claimsImageStyles } = getImageStyle(claimsImageStyle);




  const imageProps = {};

  if (preloadImage) {
    imageProps.preload = true;
  } else {
    imageProps.deferLoad = true;
  }

  return (
    <GridRow
      className={claimsStyle ? claimsClass : (rowClass, rowFlexClass)}
      vAlign={detachedImage ? 'stretch' : 'middle'}
      gutters
    >
      <GridCol base='flex-auto' md={contentColumns}>
        {title && title !== ' ' &&
          <Heading className={claimsClass} tag={headingTag} type={headingType} marginTop={headingMarginTop} {...rest}>
            {title}
          </Heading>
        }
        {subHeading && subHeading !== '' &&
          <div className={textClass}>
            {subHeading}
          </div>
        }
        {children && <div className={textClass}>{children}</div>}
        {mobileTitleBeforeImage && isMobile &&
          <GridRow md={2} className={imgWrapperClass}>
            <Image imageDomain={imageDomain} className={imageClass} {...imageProps}>
              <img className={imgClass} src={image} alt={alt} height={imageHeight} width={imageWidth} />
            </Image>
          </GridRow>
        }
        {text && text !== ' ' && <TextBody className={cx(fontClass, bodyClass)}>{text}</TextBody>}
        {(isMobile || showButtonsOnDesktop) && buttons && buttons.length ? (
          <GridRow>
            {buttons.map(
              ({ text: buttonText = 'text', href = '', linkDomain = undefined, variant = 'secondary' }) => (
                <div key={buttonText} className={btnClass} data-theme={themeEvolved ? 'evolved' : null}>
                  <LinkEnvironmentalize domain={linkDomain}>
                    <Button data-testid={buttonText} href={href} variant={variant} dynamicWidth>
                      {buttonText}
                    </Button>
                  </LinkEnvironmentalize>
                </div>
              ),
            )}
          </GridRow>
        ) : null}
        {(!isMobile || showLinkOnMobile) && linkHref && linkText &&
          <div>
            <Link href={linkHref} variant='standalone'>
              {linkText}
            </Link>
          </div>
        }
      </GridCol>
      {(!isMobile || !mobileTitleBeforeImage) &&
        <GridCol base='flex-auto' md={imageColumns} className={claimsStyle ? claimsClass : imgWrapperClass}>
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            {videoId === undefined ? (
              <Image imageDomain={imageDomain} className={imageClass} {...imageProps}>
                <img
                  className={claimsImageStyle ? `${claimsImageClass} image` : imgClass}
                  src={image}
                  alt={alt}
                  height={imageHeight}
                  width="100%"
                />
              </Image>
            ) : (
              <YouTube videoId={videoId} />
            )}
          </div>
        </GridCol>
      }
      {btnStyles}
      {rowStyles}
      {rowFlexStyles}
      {bodyStyles}
      {textStyles}
      {imgStyles}
      {imgWrapperStyles}
      {imageStyles}
      {fontStyles}
      {claimsStyles}
      {claimsImageStyles}
    </GridRow>
  );
};

ArticleWithImage.propTypes = {
  /** Article with image title */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Article text */
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Button links */
  buttons: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    href: PropTypes.string,
    linkDomain: PropTypes.string,
    variant: PropTypes.oneOf(['primary', 'secondary', 'inverse']),
  })),
  showButtonsOnDesktop: PropTypes.bool,
  showLinkOnMobile: PropTypes.bool,
  linkHref: PropTypes.string,
  linkText: PropTypes.string,
  /** Image path url */
  image: PropTypes.string,
  /** Image alt text */
  alt: PropTypes.string,
  /** Image height text */
  imageHeight: PropTypes.number,
  /** Image width text */
  imageWidth: PropTypes.number,
  /** On mobile if True image Comes before text */
  mobileImageFirst: PropTypes.bool,
  /** On desktop if set to true image comes first */
  desktopImageFirst: PropTypes.bool,
  mobileTitleBeforeImage: PropTypes.bool,
  subHeading: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  themeEvolved: PropTypes.bool,
  headingType: PropTypes.string,
  headingTag: PropTypes.string,
  textWidth: PropTypes.oneOf(['textWrap', 'normal', undefined]),
  imageColumns: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  mobileBreakpoint: PropTypes.oneOf(['sm', 'md', 'lg']),
  contentColumns: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  detachedImage: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  imageDomain: PropTypes.string,
  noTopPadding: PropTypes.bool,
  videoId: PropTypes.string,
  imageRounded: PropTypes.bool,
  useImageDimensions: PropTypes.bool,
  preloadImage: PropTypes.bool,
  headingMarginTop: PropTypes.string,
  fontSize: PropTypes.bool,
  claimsStyle: PropTypes.bool,
  claimsImageStyle: PropTypes.bool,
  claimsTextStyle: PropTypes.bool,
};

export default ArticleWithImage;
